import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Toaster, toast } from 'sonner';
import Footer from '../components/Footer';
import { Mail, Lock, AlertCircle, Fingerprint } from 'lucide-react';
import { isPasskeySupported, loginWithPasskey } from '../utils/passkey';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const { login, setSession } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Accesso fallito. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeyLogin = async () => {
    if (!isPasskeySupported()) {
      toast.error('Passkey non supportato da questo browser');
      return;
    }
    setError('');
    setPasskeyLoading(true);
    try {
      const data = await loginWithPasskey(axios, email);
      if (setSession) {
        setSession(data.access_token, data.user);
      } else {
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        axios.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
      }
      toast.success('Accesso con Passkey riuscito');
      window.location.href = '/dashboard';
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Accesso con Passkey fallito';
      setError(typeof msg === 'string' ? msg : 'Accesso con Passkey fallito');
    } finally {
      setPasskeyLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dot-grid-bg flex flex-col p-4">
      <Toaster position="top-right" richColors />
      <div className="flex-1 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <motion.div
            className="inline-flex items-center justify-center w-24 h-24 rounded-2xl bg-white shadow-lg ring-1 ring-slate-200 mb-4 overflow-hidden"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 400 }}
          >
            <img src="/86bit-logo.jpg" alt="86BIT" className="w-full h-full object-contain p-1" data-testid="login-logo" />
          </motion.div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Customer Scout</h1>
          <p className="text-slate-600 mt-2">Lead generation iper-locale per il B2B italiano</p>
        </div>

        <Card className="border-slate-200 shadow-lg">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl tracking-tight">Bentornato</CardTitle>
            <CardDescription>Inserisci le tue credenziali per accedere</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="flex items-center gap-2 p-3 bg-rose-50 border border-rose-200 rounded-lg text-rose-700 text-sm"
                >
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </motion.div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="nome@azienda.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-11"
                    data-testid="login-email-input"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="La tua password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-11"
                    data-testid="login-password-input"
                    required
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="spinner" />
                    Accesso in corso...
                  </span>
                ) : (
                  'Accedi'
                )}
              </Button>
            </form>

            {isPasskeySupported() && (
              <>
                <div className="my-4 flex items-center gap-3">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span className="text-xs text-slate-400 uppercase tracking-wider">oppure</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handlePasskeyLogin}
                  disabled={passkeyLoading}
                  className="w-full h-11 border-slate-300"
                  data-testid="passkey-login-btn"
                >
                  {passkeyLoading ? (
                    <span className="flex items-center gap-2"><span className="spinner" /> Verifica...</span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Fingerprint className="w-4 h-4" /> Accedi con Passkey
                    </span>
                  )}
                </Button>
              </>
            )}

            <div className="mt-6 text-center text-xs text-slate-500">
              Accesso riservato. Per un nuovo account contatta l'amministratore.
            </div>
          </CardContent>
        </Card>
      </motion.div>
      </div>
      <Footer />
    </div>
  );
};

export default LoginPage;
